import React, { useContext, useEffect, useState } from "react";
import PagePreview from "./Preview";
import { useSelector } from "react-redux";
import { FullGridContext } from "..";
import { getData } from "~/functions/gridFunctions";
import PageContainer from "~/components/PageContainer";
import { format, parseISO } from "date-fns";
import { useLocation } from "react-router-dom";
import ModalFilters, { getStoredFilters } from "./ModalFilters";
import { Pin } from "lucide-react";
import api from "~/services/api";

export default function I20Pendings() {
  const filial = useSelector((state) => state.auth.filial);
  const defaultOrderBy = { column: "created_at", asc: false };
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [filtersPinned, setFiltersPinned] = useState(
    () => localStorage.getItem("i20pendings_filters_pinned") === "true"
  );
  const [applyingFilters, setApplyingFilters] = useState(false);
  const [activeFilterQuery, setActiveFilterQuery] = useState(null);
  const defaultGridHeader = [
    {
      title: "Full Name",
      name: "type",
      type: "text",
      filter: false,
    },
    {
      title: "Process",
      name: "type",
      type: "text",
      filter: true,
    },
    {
      title: "Substatus",
      name: "type",
      type: "text",
      filter: true,
    },
    {
      title: "Registration Number",
      name: "type",
      type: "text",
      filter: false,
    },
    {
      title: "Category",
      name: "type",
      type: "text",
      filter: true,
    },
    {
      title: "Application",
      name: "type",
      type: "text",
      filter: true,
    },
    {
      title: "Status",
      name: "subject",
      type: "text",
      filter: true,
    },
    {
      title: "Enrollment Date",
      name: "type",
      type: "text",
      filter: false,
    },
    {
      title: "Agent",
      name: "type",
      type: "text",
      filter: false,
    },
  ];

  const {
    accessModule,
    activeFilters,
    gridData,
    gridDetails,
    gridHeader,
    handleFilters,
    handleOpened,
    limit,
    loadingData,
    opened,
    orderBy,
    page,
    pages,
    search,
    setActiveFilters,
    setGridData,
    setGridDetails,
    setGridHeader,
    setLimit,
    setLoadingData,
    setOpened,
    setOrderBy,
    setPage,
    setPages,
    setSearch,
    setTotalRows,
    setSuccessfullyUpdated,
    successfullyUpdated,
    totalRows,
  } = useContext(FullGridContext);

  const pageAccess = accessModule.children.find(
    (el) => el.alias === "i20-pendings"
  );

  const { pathname } = useLocation();

  const mapI20Pendings = (data) =>
    data.map(
      ({ status, enrollments, solicitation_date, canceled_at }, index) => {
        const { students, application } = enrollments;
        const {
          name,
          last_name,
          category,
          registration_number,
          processtypes,
          processsubstatuses,
          agent,
        } = students;
        return {
          show: true,
          id: enrollments.id,
          fields: [
            name + " " + last_name,
            processtypes?.name,
            processsubstatuses?.name,
            registration_number,
            category,
            application,
            status,
            format(parseISO(solicitation_date), "MM/dd/yyyy"),
            agent?.name,
          ],
          selectable: true,
          canceled: canceled_at,
          page: Math.ceil((index + 1) / limit),
        };
      }
    );

  async function loader() {
    setLoadingData(true);
    const stored = getStoredFilters();
    const extraParams = {};
    if (stored) {
      if (stored.process) extraParams.type = stored.process;
      if (stored.substatus) extraParams.substatus = stored.substatus;
      if (stored.registrationNumber)
        extraParams.registration_number = stored.registrationNumber;
      if (stored.category) extraParams.category = stored.category;
      if (stored.applicationStatus) extraParams.status = stored.applicationStatus;
      if (stored.status) extraParams.i20_status = stored.status;
      if (stored.agent) extraParams.agent = stored.agent;
      if (stored.enrollmentDateFrom)
        extraParams.enrollment_date_from = stored.enrollmentDateFrom;
      if (stored.enrollmentDateTo)
        extraParams.enrollment_date_to = stored.enrollmentDateTo;
    }
    const data = await getData("i20pendings", {
      limit,
      page,
      orderBy,
      setPages,
      setGridData,
      search,
      defaultGridHeader,
      defaultOrderBy,
      setGridDetails,
      pathname,
      extraParams: Object.keys(extraParams).length ? extraParams : null,
    });

    if (!data) {
      return;
    }
    setGridData(mapI20Pendings(data));
    setLoadingData(false);
  }

  useEffect(() => {
    setActiveFilters([]);
    setSearch("");
  }, []);

  const applyFiltersToGrid = async (queryString) => {
    setApplyingFilters(true);
    setLoadingData(true);
    try {
      setActiveFilters([]);
      setActiveFilterQuery(queryString);
      const { data } = await api.get(queryString);
      const pagesCalc = Math.ceil((data?.totalRows || 0) / limit) || 1;
      setPages(pagesCalc);
      setGridDetails({
        totalRows: data?.totalRows || 0,
        pages: pagesCalc,
      });
      setGridData(mapI20Pendings(data?.rows || []));
    } catch (err) {
      console.log(err);
    } finally {
      setApplyingFilters(false);
      setLoadingData(false);
    }
  };

  useEffect(() => {
    setActiveFilterQuery(null);

    if (!activeFilterQuery) {
      loader();
    }
  }, [opened, filial, search, limit, page]);

  useEffect(() => {
    if (!orderBy) return;

    if (activeFilterQuery) {
      setLoadingData(true);

      const url = new URL(activeFilterQuery, window.location.origin);
      const params = new URLSearchParams(url.search);

      params.set("orderBy", orderBy.column);
      params.set("orderASC", orderBy.asc ? "ASC" : "DESC");

      const newQueryString = `${url.pathname}?${params.toString()}`;

      api
        .get(newQueryString)
        .then(({ data }) => {
          const pagesCalc = Math.ceil((data?.totalRows || 0) / limit) || 1;
          setPages(pagesCalc);
          setGridDetails({
            totalRows: data?.totalRows || 0,
            pages: pagesCalc,
          });
          setGridData(mapI20Pendings(data?.rows || []));
        })
        .catch((err) => console.log(err))
        .finally(() => setLoadingData(false));
    } else {
      loader();
    }
  }, [orderBy]);

  return (
    <>
      <ModalFilters
        opened={filtersModalOpen}
        applying={applyingFilters}
        onClose={() => {
          setFiltersModalOpen(false);
          setFiltersPinned(
            localStorage.getItem("i20pendings_filters_pinned") === "true"
          );
        }}
        onApplyFilters={applyFiltersToGrid}
      />
      <PageContainer
        FullGridContext={FullGridContext}
        PagePreview={PagePreview}
        pageAccess={pageAccess}
        handleNew={false}
        defaultGridHeader={defaultGridHeader}
        filtersModal={{
          onOpen: () => setFiltersModalOpen(true),
          viewSelect: filtersPinned ? (
            <div className="flex items-center gap-1 text-orange-500 text-xs font-medium">
              <Pin size={13} className="fill-orange-500" />
              Filters Applied
            </div>
          ) : null,
        }}
      />
    </>
  );
}
